import React, { forwardRef } from "react";
import { Button, ButtonProps, SxProps, Theme } from "@mui/material";

import { mergeSx } from "./styles";

/**
 * Standard action button for the application.
 *
 * `intent` expresses the emphasis level of the action, not its function
 * (one `primary` per visual area, the rest lower):
 * - `"primary"` (default) — the main action of the area: save, submit, generate.
 * - `"tonal"` — secondary: export, import, add a row, cancel in a dialog.
 * - `"secondary"` — low emphasis: reset, close, inline actions. Text only.
 * - `"danger"` — destructive: delete. Contained + error color.
 *
 * All other MUI ButtonProps (size, disabled, loading, startIcon, sx…) are passed through.
 */
export type AppButtonIntent = "primary" | "tonal" | "secondary" | "danger";
type Intent = AppButtonIntent;

type IntentConfig = {
  variant: ButtonProps["variant"];
  color?: ButtonProps["color"];
  sx?: SxProps<Theme>;
};

const INTENTS: Record<Intent, IntentConfig> = {
  primary: { variant: "contained", color: "primary" },
  tonal: {
    variant: "contained",
    sx: {
      backgroundColor: "button.tonalBackground",
      color: "text.primary",
      "&:hover": { backgroundColor: "button.tonalBackgroundHover" },
    },
  },
  secondary: { variant: "text", color: "primary" },
  danger: { variant: "contained", color: "error" },
};

interface AppButtonProps extends Omit<ButtonProps, "variant" | "color"> {
  intent?: Intent;
  component?: React.ElementType;
  // Passed through when `component` is a router Link.
  to?: string;
  target?: string;
  rel?: string;
}

// Forwards the ref so MUI can anchor on the button (Tooltip, Menu, Popover).
const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  function AppButton({ intent = "primary", sx, children, ...props }, ref) {
    const { variant, color, sx: intentSx } = INTENTS[intent];

    return (
      <Button
        ref={ref}
        variant={variant}
        color={color}
        {...props}
        sx={[
          { textTransform: "none", boxShadow: "unset" },
          ...mergeSx(intentSx),
          ...mergeSx(sx),
        ]}
      >
        {children}
      </Button>
    );
  }
);

export default AppButton;
